import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  validateCertificateZipCode,
} from '../../../../actions';
import {
  isValidZipCode,
  removeEmojis,
  validateZipCode,
} from '../../../../services/utils';
import {
  CERTIFICATE_LINKING_STEPS,
} from '../../petDemographics.constants';

const ZIP_CODE_MAX_LENGTH = 5;

const useVerifyScreenForm = ({
  email = '',
  onVerify = () => { },
  setCurrentStep = () => { },
}) => {
  const { t } = useTranslation('petDemos');

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');

  const shouldVerifyUserRef = useRef(false);

  const dispatch = useDispatch();
  const { isValid, isValidating } = useSelector(
    ({ petDemographics: { zipCodeValidation } }) => zipCodeValidation,
    shallowEqual,
  );

  useEffect(() => {
    if (isValidating || isValid === null || !shouldVerifyUserRef.current) {
      return;
    }

    shouldVerifyUserRef.current = false;

    if (isValid) {
      onVerify({ email, firstName, lastName, zipCode });
    } else {
      setZipCodeError(t('certificateLinking.verifyDetails.invalidZipCode'));
    }
  }, [isValid, isValidating, email, firstName, lastName, onVerify, t, zipCode]);

  const handleFirstNameChange = (event) => {
    const cleanText = removeEmojis(event.target.value);
    setFirstName(cleanText);
  };

  const handleOnBlurFirstName = () => {
    if (firstName.trim() !== '') {
      setFirstNameError('');
    } else {
      setFirstNameError(t('certificateLinking.verifyDetails.requiredField'));
    }
  };

  const handleLastNameChange = (event) => {
    const cleanText = removeEmojis(event.target.value);
    setLastName(cleanText);
  };

  const handleOnBlurLastName = () => {
    if (lastName.trim() !== '') {
      setLastNameError('');
    } else {
      setLastNameError(t('certificateLinking.verifyDetails.requiredField'));
    }
  };

  const handleZipCodeChange = (event) => {
    const { value } = event.target;
    const normalizedZipCode = validateZipCode(value);
    setZipCode(normalizedZipCode);

    if (isValidZipCode(normalizedZipCode)) {
      setZipCodeError('');
    }
  };

  const handleOnBlurZipCode = () => {
    if (zipCode === '') {
      setZipCodeError(t('certificateLinking.verifyDetails.requiredField'));
    } else if (!isValidZipCode(zipCode)) {
      setZipCodeError(t('certificateLinking.verifyDetails.invalidZipCode'));
    } else {
      setZipCodeError('');
    }
  };

  const noErrors = useMemo(() => !firstNameError && !lastNameError
    && !zipCodeError && !isValidating,
  [firstNameError, lastNameError, zipCodeError, isValidating]);

  const handleSubmit = (event) => {
    event.preventDefault();
    let hasError = false;

    if (firstName.trim() === '') {
      setFirstNameError(t('certificateLinking.verifyDetails.requiredField'));
      hasError = true;
    }

    if (lastName.trim() === '') {
      setLastNameError(t('certificateLinking.verifyDetails.requiredField'));
      hasError = true;
    }

    if (zipCode === '') {
      setZipCodeError(t('certificateLinking.verifyDetails.requiredField'));
      hasError = true;
    } else if (!isValidZipCode(zipCode)) {
      setZipCodeError(t('certificateLinking.verifyDetails.invalidZipCode'));
      hasError = true;
    }

    if (!hasError) {
      shouldVerifyUserRef.current = true;
      dispatch(validateCertificateZipCode({ zipCode }));
    }
  };

  const handleVerifyMouseDown = (event) => {
    event.preventDefault();
  };

  const handleSkip = () => {
    setCurrentStep(CERTIFICATE_LINKING_STEPS.ALREADY_REGISTERED);
  };

  const firstNamePlaceholder = t(
    'certificateLinking.verifyDetails.firstNamePlaceholder',
  );
  const lastNamePlaceholder = t(
    'certificateLinking.verifyDetails.lastNamePlaceholder',
  );

  return {
    firstName,
    firstNameError,
    firstNamePlaceholder,
    handleFirstNameChange,
    handleLastNameChange,
    handleOnBlurFirstName,
    handleOnBlurLastName,
    handleOnBlurZipCode,
    handleSkip,
    handleSubmit,
    handleVerifyMouseDown,
    handleZipCodeChange,
    lastName,
    lastNameError,
    lastNamePlaceholder,
    noErrors,
    ZIP_CODE_MAX_LENGTH,
    zipCode,
    zipCodeError,
  };
};

export { useVerifyScreenForm };
